import KeyboardTabOutlined from '@mui/icons-material/KeyboardTabOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { CleaningMission, Unit } from '@silbo/core';
import { ClockIcon, colors, CommentIcon } from '@silbo/ui';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext';
import { ago } from '../utils/elapsed';
import { describePlannedEntry } from '../utils/planned-entry';
import CleaningMissionPrioritySquare from './CleaningMissionPrioritySquare';
import CleaningMissionReserveButton from './CleaningMissionReserveButton';

type CleaningMissionCardProps = {
  mission: CleaningMission;
  unit?: Unit;
  now: Date;
};

const InProgressTag = ({ children }: { children: ReactNode }) => (
  <Box
    component="span"
    sx={{
      alignSelf: 'flex-start',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      marginTop: '2px',
      paddingX: 2,
      paddingY: '3px',
      borderRadius: '6px',
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      bgcolor: colors.green[5],
      color: colors.green[90],
    }}
  >
    {children}
  </Box>
);

const iconSx = { fontSize: 13, flex: 'none' } as const;

const CleaningMissionCard = ({ mission, unit, now }: CleaningMissionCardProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const { user } = useAuthenticationContext();
  const isReservedByMe = mission.status === 'todo' && !!user && mission.assignee?.id === user.id;
  const plannedEntry = describePlannedEntry(mission.bed.nextSlotAt, now);
  const lastComment = mission.comments?.at(-1);

  const inProgressByAnother = mission.status === 'in-progress' && !!mission.cleaner && mission.cleaner.id !== user?.id;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={3}
      sx={{
        paddingX: 4,
        paddingY: 4,
        borderBottom: `1px solid ${colors.grey[10]}`,
        bgcolor: isReservedByMe ? colors.blue[5] : 'common.white',
      }}
    >
      <Link
        to="/environmental-cleaning/missions/$id"
        params={{ id: mission.id }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flex: 1,
          minWidth: 0,
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <CleaningMissionPrioritySquare priority={mission.priority} />
        <Stack flex={1} minWidth={0} gap="3px">
          <Stack direction="row" alignItems="baseline" gap={2} minWidth={0}>
            <Typography sx={{ flex: 'none', fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {mission.bed.name}
            </Typography>
            {unit && (
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {unit.name}
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={3}
            sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <ClockIcon sx={iconSx} />
              {ago(t, mission.requestedAt, now)}
            </Stack>
            {plannedEntry && (
              <Stack direction="row" alignItems="center" gap={1} sx={{ color: 'text.primary', fontWeight: 600 }}>
                <KeyboardTabOutlined sx={iconSx} />
                {t(`missions.row.plannedEntry.${plannedEntry.kind}`, plannedEntry)}
              </Stack>
            )}
          </Stack>
          {lastComment && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              minWidth={0}
              sx={{ fontSize: 13, color: colors.grey[90] }}
            >
              <CommentIcon sx={{ ...iconSx, color: 'text.secondary' }} />
              <Box
                component="span"
                sx={{
                  fontStyle: 'italic',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {lastComment.text}
              </Box>
            </Stack>
          )}
          {inProgressByAnother && (
            <InProgressTag>{t('missions.row.inProgressBy', { name: mission.cleanerName ?? '' })}</InProgressTag>
          )}
        </Stack>
      </Link>
      <CleaningMissionReserveButton mission={mission} />
    </Stack>
  );
};

export default CleaningMissionCard;
